import { useForm } from '@tanstack/react-form';
import {
  Bot,
  Braces,
  Check,
  Copy,
  CornerDownRight,
  Tag,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@sara/ui/components/button';
import { Input } from '@sara/ui/components/input';
import { Textarea } from '@sara/ui/components/textarea';
import { Field, FieldContent, FieldError, FieldLabel } from '@sara/ui/components/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sara/ui/components/select';
import { toast } from '@sara/ui/components/sonner';
import { SHOW_ORG_LOGO_LABELS, type ShowOrgLogoChoice } from '../../lib/show-org-logo';
import { DEFAULT_CONSENT_CONFIG_FORM_VALUE } from '../../lib/consent-fields';
import { CandidateConsentFields } from '../CandidateConsentFields';
import { createInterviewTypeFormSchema, type CreateInterviewTypeFormValues } from './schemas';

/** Mirrors the server-side slugify in apps/api InterviewTypesService. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function SlugPreview({ name, disabled }: { name: string; disabled: boolean }) {
  const slug = slugify(name);
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!slug) return;
    try {
      await navigator.clipboard.writeText(slug);
      setCopied(true);
      toast.success('Copied slug');
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Copy failed — select and copy manually.');
    }
  }

  return (
    <div className="group/slug flex h-5 items-center gap-2 pl-0.5 pt-0.5 text-[11.5px]">
      <CornerDownRight
        className="h-3 w-3 shrink-0 text-muted-foreground/50"
        strokeWidth={1.75}
        aria-hidden
      />
      <span className="font-medium uppercase tracking-[0.12em] text-muted-foreground/70">slug</span>
      {slug ? (
        <>
          <code className="truncate font-mono text-[12px] tabular-nums text-foreground/75">
            {slug}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            disabled={disabled}
            aria-label="Copy slug"
            title="Copy slug"
            className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground/60 opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus-visible:opacity-100 group-hover/slug:opacity-100 disabled:cursor-not-allowed"
          >
            {copied ? (
              <Check className="h-3 w-3 text-success" strokeWidth={2} />
            ) : (
              <Copy className="h-3 w-3" strokeWidth={1.75} />
            )}
          </button>
        </>
      ) : (
        <span className="italic text-muted-foreground/50">generated from the name</span>
      )}
    </div>
  );
}

export interface SelectorOption {
  id: string;
  name: string;
  latestVersionId: string;
  latestVersion: number;
}

interface CreateInterviewTypeFormProps {
  onSubmit: (values: CreateInterviewTypeFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
  /**
   * Whether the org has an uploaded logo. When false, the candidate logo
   * select shows a hint prompting the admin to upload a logo in Branding first.
   */
  orgHasLogo: boolean;
  extractions?: SelectorOption[];
  rubrics?: SelectorOption[];
}

const subLabelClassName = 'text-[12px] font-medium text-foreground/80';
const fieldHelpClassName = '-mt-1 text-[12.5px] leading-snug text-muted-foreground';
const textareaClassName =
  'flex w-full rounded-md border border-border/70 bg-background px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-foreground/90 ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

function SectionCard({
  icon: Icon,
  title,
  hint,
  trailing,
  children,
}: {
  icon: LucideIcon;
  title: string;
  hint?: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border/70 bg-card">
      <header className="flex items-start justify-between gap-3 border-b border-border/60 bg-muted/30 px-4 py-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background text-foreground/70">
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <div className="flex flex-col min-w-0 pt-0.5">
            <h3 className="text-[14px] font-semibold leading-tight text-foreground">{title}</h3>
            {hint && <p className="text-[12.5px] leading-snug text-muted-foreground">{hint}</p>}
          </div>
        </div>
        {trailing && <div className="shrink-0">{trailing}</div>}
      </header>
      <div className="p-4 sm:p-5 space-y-5">{children}</div>
    </section>
  );
}

export function CreateInterviewTypeForm({
  onSubmit,
  onCancel,
  isPending,
  orgHasLogo,
  extractions = [],
  rubrics = [],
}: CreateInterviewTypeFormProps) {
  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      systemPrompt: '',
      firstMessage: '',
      // 'none' = no selection; containers map this to undefined before sending to the API
      extractionVersionId: 'none' as string,
      rubricVersionId: 'none' as string,
      candidateTitle: '',
      candidateDescription: '',
      candidateEndOfCallNotes: '',
      candidateDeclinedMessage: '',
      candidateShowOrgLogo: 'default' as ShowOrgLogoChoice,
      consentConfig: DEFAULT_CONSENT_CONFIG_FORM_VALUE,
    },
    validators: {
      onSubmit: createInterviewTypeFormSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      autoComplete="off"
      className="space-y-5"
    >
      {/* Details — labels and a derived slug. None of these fields reach the
          agent; they exist for humans (lists, search, shared links). */}
      <SectionCard
        icon={Tag}
        title="Details"
        hint="How your team labels and recognizes this template in lists, search, and links."
      >
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-5 gap-y-4">
          <form.Field name="name">
            {(field) => {
              const hasError = field.state.meta.errors.length > 0;
              return (
                <Field data-invalid={hasError || undefined}>
                  <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                    Name <span className="text-destructive">*</span>
                  </FieldLabel>
                  <FieldContent>
                    <Input
                      id={field.name}
                      placeholder="e.g. Technical screen"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={isPending}
                      aria-invalid={hasError}
                      autoComplete="off"
                      className="h-9"
                    />
                    <FieldError errors={field.state.meta.errors} />
                    <SlugPreview name={field.state.value} disabled={isPending} />
                  </FieldContent>
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="description">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                  Description
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={field.name}
                    placeholder="Brief description of this template"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending}
                    autoComplete="off"
                    className="h-9"
                  />
                </FieldContent>
              </Field>
            )}
          </form.Field>
        </div>
      </SectionCard>

      {/* Agent — every template owns a NEW dedicated ElevenLabs agent, duplicated
          from the preconfigured base agent (voice + model ready) on create. The
          prompt below is written onto that fresh agent, which becomes the source
          of truth (strict 1:1 template↔agent — editing one never affects another). */}
      <SectionCard
        icon={Bot}
        title="Agent"
        hint="A dedicated ElevenLabs agent is created for this template. Write its prompt here."
      >
        <div className="space-y-5">
          <form.Field name="systemPrompt">
            {(field) => {
              const hasError = field.state.meta.errors.length > 0;
              return (
                <Field data-invalid={hasError || undefined}>
                  <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                    System prompt <span className="text-destructive">*</span>
                  </FieldLabel>
                  <p className={fieldHelpClassName}>
                    Written to the template&rsquo;s new agent when it&rsquo;s created.
                  </p>
                  <FieldContent>
                    <Textarea
                      id={field.name}
                      placeholder="You are Sara, a warm and efficient interviewer for hospitality roles…"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={isPending}
                      aria-invalid={hasError}
                      rows={7}
                      className="max-h-[40vh] min-h-[9rem] resize-y overflow-y-auto font-mono text-[12.5px]"
                      data-testid="system-prompt"
                    />
                    <FieldError errors={field.state.meta.errors} />
                  </FieldContent>
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="firstMessage">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                  First message
                </FieldLabel>
                <p className={fieldHelpClassName}>
                  The agent&rsquo;s opening line. Leave blank to let the agent open on its own.
                </p>
                <FieldContent>
                  <Input
                    id={field.name}
                    placeholder="Hi {{first_name}}! Thanks for taking the time today…"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending}
                    autoComplete="off"
                    className="h-9"
                    data-testid="first-message"
                  />
                </FieldContent>
              </Field>
            )}
          </form.Field>
        </div>
      </SectionCard>

      {/* Analysis — extraction and rubric selectors. */}
      <SectionCard
        icon={Braces}
        title="Analysis"
        hint="Optional post-call extraction and scoring to run on each conversation."
      >
        <form.Field name="extractionVersionId">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                Extraction (optional)
              </FieldLabel>
              <p className={fieldHelpClassName}>
                Runs after the call to extract structured data from the transcript.
              </p>
              <FieldContent>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v)}
                  disabled={isPending}
                >
                  <SelectTrigger id={field.name} className="h-9">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {extractions.map((e) => (
                      <SelectItem key={e.latestVersionId} value={e.latestVersionId}>
                        {e.name} · v{e.latestVersion}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="rubricVersionId">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                Rubric (optional)
              </FieldLabel>
              <p className={fieldHelpClassName}>
                Scores each conversation against a rubric after the call completes.
              </p>
              <FieldContent>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v)}
                  disabled={isPending}
                >
                  <SelectTrigger id={field.name} className="h-9">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {rubrics.map((r) => (
                      <SelectItem key={r.latestVersionId} value={r.latestVersionId}>
                        {r.name} · v{r.latestVersion}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldContent>
            </Field>
          )}
        </form.Field>
      </SectionCard>

      {/* Candidate experience — what the candidate sees around the call. */}
      <SectionCard
        icon={UserRound}
        title="Candidate experience"
        hint="What the candidate sees before, during, and after the conversation."
      >
        <form.Field name="candidateTitle">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0;
            return (
              <Field data-invalid={hasError || undefined}>
                <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                  Call title <span className="text-destructive">*</span>
                </FieldLabel>
                <FieldContent>
                  <Input
                    id={field.name}
                    placeholder="e.g. Bartender screening call"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending}
                    aria-invalid={hasError}
                    autoComplete="off"
                    className="h-9"
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>

        <form.Field name="candidateDescription">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0;
            return (
              <Field data-invalid={hasError || undefined}>
                <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                  Lobby description <span className="text-destructive">*</span>
                </FieldLabel>
                <FieldContent>
                  <textarea
                    id={field.name}
                    placeholder="What the candidate sees while waiting to start…"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending}
                    aria-invalid={hasError}
                    rows={4}
                    className={textareaClassName}
                    autoComplete="off"
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>

        <form.Field name="candidateEndOfCallNotes">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0;
            return (
              <Field data-invalid={hasError || undefined}>
                <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                  End-of-call notes <span className="text-destructive">*</span>
                </FieldLabel>
                <FieldContent>
                  <textarea
                    id={field.name}
                    placeholder="What the candidate sees once the conversation ends…"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending}
                    aria-invalid={hasError}
                    rows={4}
                    className={textareaClassName}
                    autoComplete="off"
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>

        <form.Field name="candidateDeclinedMessage">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0;
            return (
              <Field data-invalid={hasError || undefined}>
                <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                  Declined message <span className="text-destructive">*</span>
                </FieldLabel>
                <p className={fieldHelpClassName}>
                  Shown on the &ldquo;Got it&rdquo; screen when a candidate declines. Supports{' '}
                  <code>**bold**</code>; leave a blank line to start a new paragraph.
                </p>
                <FieldContent>
                  <textarea
                    id={field.name}
                    placeholder="What the candidate sees after declining the conversation…"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={isPending}
                    aria-invalid={hasError}
                    rows={4}
                    className={textareaClassName}
                    autoComplete="off"
                  />
                  <FieldError errors={field.state.meta.errors} />
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>

        <form.Field name="candidateShowOrgLogo">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                Organization logo
              </FieldLabel>
              <FieldContent>
                <Select
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v as typeof field.state.value)}
                  disabled={isPending}
                >
                  <SelectTrigger id={field.name} className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{SHOW_ORG_LOGO_LABELS.default}</SelectItem>
                    <SelectItem value="show">{SHOW_ORG_LOGO_LABELS.show}</SelectItem>
                    <SelectItem value="hide">{SHOW_ORG_LOGO_LABELS.hide}</SelectItem>
                  </SelectContent>
                </Select>
                <p className={fieldHelpClassName}>
                  {orgHasLogo
                    ? 'Org default is managed in Settings → Branding.'
                    : 'Upload an organization logo in Settings → Branding for the logo to appear.'}
                </p>
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="consentConfig">
          {(field) => (
            <Field>
              <FieldLabel className={subLabelClassName}>Consent &amp; secondary link</FieldLabel>
              <FieldContent>
                <CandidateConsentFields
                  value={field.state.value}
                  onChange={(next) => field.handleChange(next)}
                  disabled={isPending}
                />
              </FieldContent>
            </Field>
          )}
        </form.Field>
      </SectionCard>

      {/* Sticky footer — keeps Submit/Cancel always reachable on long forms. */}
      <div className="sticky bottom-4 mb-4 mt-2 rounded-lg border border-border/70 bg-background/95 backdrop-blur px-4 py-2.5 flex items-center justify-between gap-3">
        <span className="text-[12px] text-muted-foreground hidden sm:inline">
          Versions are immutable — edits create a new version.
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <Button
            type="button"
            variant="ghost"
            shape="pill"
            size="sm"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="tinted"
            shape="pill"
            size="sm"
            disabled={isPending}
            className="gap-1.5"
          >
            {isPending ? 'Creating…' : 'Create template'}
          </Button>
        </div>
      </div>
    </form>
  );
}
